import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  fetchBundlePointer,
  fetchChannelPointers,
  fetchReleases,
  type BundlePointer,
  type ChannelName,
  type ChannelPointers,
  type ComponentName,
  type ComponentPointer,
  type Release,
  formatBytes,
  ApiError,
} from "@/lib/api";
import { formatDate } from "@/lib/time";
import { CopyBox } from "@/components/CopyBox";
import { ReleaseNotesBody } from "@/components/ReleaseNotesBody";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "not-found" }
  | {
      status: "bundle";
      pointer: BundlePointer;
      pointers: ChannelPointers | null;
    }
  | { status: "component"; release: Release };

/**
 * Bundle-first detail page. Resolution order:
 *   1. `/releases/:channel/:version` → try the channel's current bundle
 *      pointer. If that version matches, we're done.
 *   2. Otherwise scan the release index. If a bundle row matches, render
 *      as a historical bundle (no component chips — `componentVersions`
 *      lives on the index row).
 *   3. If a per-component row matches, render the component-detail view.
 *   4. Otherwise 404.
 *
 * Legacy `/releases/:version` URLs (no channel) fall straight to the
 * index scan — the customer-facing route is always channel-scoped now
 * but we keep the old shape alive so inbound links from elsewhere still
 * land somewhere useful.
 */
export function ReleaseDetail() {
  const params = useParams<{ channel?: string; version: string }>();
  const [searchParams] = useSearchParams();
  const componentParam = searchParams.get("component");

  const [state, setState] = useState<LoadState>({ status: "loading" });

  const load = useCallback(async () => {
    const version = params.version;
    if (!version) {
      setState({ status: "not-found" });
      return;
    }
    setState({ status: "loading" });

    const channelParam = params.channel;
    const channel: ChannelName | null =
      channelParam === "stable" || channelParam === "beta"
        ? channelParam
        : null;

    try {
      // Fast path: channel-scoped URL with the version matching the live
      // pointer. Also gives us component pointers for the "pinned
      // component" deep-links.
      if (channel && !componentParam) {
        const pointer = await fetchBundlePointer(channel);
        if (pointer && pointer.version === version) {
          const pointers = await fetchChannelPointers(channel).catch(
            () => null,
          );
          setState({ status: "bundle", pointer, pointers });
          return;
        }
      }

      // Slow path: scan the index. Match either a specific component or
      // the bundle row depending on what we were asked for.
      const { releases } = await fetchReleases();
      const match = releases.find((r) => {
        if (r.version !== version) return false;
        if (channel && r.channel !== channel) return false;
        if (componentParam) return r.component === componentParam;
        return true;
      });
      if (!match) {
        setState({ status: "not-found" });
        return;
      }
      if (match.component === "bundle") {
        // Historical bundle row — build a pseudo-pointer from the index
        // entry so the bundle view still renders. No per-component
        // pointers here; the detail page gracefully degrades.
        const bundleArtifact = match.artifacts[0];
        const pseudo: BundlePointer = {
          version: match.version,
          channel: match.channel,
          releasedAt: match.date,
          rolloutPercent: match.rolloutPercent ?? 100,
          rolloutStartedAt: match.rolloutStartedAt,
          bundle: {
            url: bundleArtifact?.url ?? "",
            sha256: bundleArtifact?.sha256 ?? "",
            signature: "",
            sizeBytes: bundleArtifact?.size ?? 0,
          },
          manifest: { url: "", sha256: "" },
          componentVersions: (match.componentVersions ??
            {}) as Record<ComponentName, string>,
          releaseNotes: match.notes || null,
          customerReleaseNotes: match.customerNotes,
          commitSha: null,
          ciPipelineId: null,
        };
        setState({ status: "bundle", pointer: pseudo, pointers: null });
        return;
      }
      setState({ status: "component", release: match });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Server error (${err.status})`
          : "Failed to load release details.";
      setState({ status: "error", message });
    }
  }, [params.channel, params.version, componentParam]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-4">
          <div className="skeleton h-4 w-32 rounded" />
          <div className="skeleton h-10 w-48 rounded-lg" />
          <div className="skeleton h-4 w-64 rounded" />
          <div className="skeleton h-48 rounded-xl mt-6" />
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <ErrorState message={state.message} onRetry={load} />
    );
  }

  if (state.status === "not-found") {
    return <NotFoundState version={params.version} />;
  }

  if (state.status === "bundle") {
    return (
      <BundleDetailView pointer={state.pointer} pointers={state.pointers} />
    );
  }

  return <ComponentDetailView release={state.release} />;
}

function BackLink() {
  return (
    <Link
      to="/releases"
      className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300 mb-6 transition-colors"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      All releases
    </Link>
  );
}

function BundleDetailView({
  pointer,
  pointers,
}: {
  pointer: BundlePointer;
  pointers: ChannelPointers | null;
}) {
  const [showTechnical, setShowTechnical] = useState(false);
  const customerNotes = pointer.customerReleaseNotes;
  const technicalNotes = pointer.releaseNotes;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 animate-fade-in">
      <BackLink />

      <div className="mb-8">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30">
            Bundle
          </span>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              pointer.channel === "stable"
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
            }`}
          >
            {pointer.channel}
          </span>
          {pointer.rolloutPercent !== undefined &&
            pointer.rolloutPercent < 100 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20">
                {pointer.rolloutPercent}% rollout
              </span>
            )}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-mono mb-2">
          v{pointer.version}
        </h1>
        <p className="text-zinc-400">{formatDate(pointer.releasedAt)}</p>
      </div>

      <div className="space-y-8">
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="font-semibold text-zinc-100 mb-4">What's new</h2>
          <ReleaseNotesBody notes={customerNotes || technicalNotes} />
          {technicalNotes && customerNotes && technicalNotes !== customerNotes && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <button
                onClick={() => setShowTechnical(!showTechnical)}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors inline-flex items-center gap-1"
              >
                <svg
                  className={`w-3 h-3 transition-transform ${showTechnical ? "rotate-90" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {showTechnical ? "Hide" : "Show"} technical notes
              </button>
              {showTechnical && (
                <div className="mt-3">
                  <ReleaseNotesBody notes={technicalNotes} />
                </div>
              )}
            </div>
          )}
        </section>

        {pointer.componentVersions &&
          Object.keys(pointer.componentVersions).length > 0 && (
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="font-semibold text-zinc-100 mb-4">
                Pinned components
              </h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {Object.entries(pointer.componentVersions).map(([name, ver]) => {
                  const compName = name as ComponentName;
                  const compPointer =
                    pointers?.components[compName] ?? null;
                  return (
                    <ComponentChipLink
                      key={name}
                      name={name}
                      version={ver}
                      channel={pointer.channel}
                      pointer={compPointer}
                    />
                  );
                })}
              </div>
            </section>
          )}

        {pointer.bundle.url && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-semibold text-zinc-100 mb-4">Bundle artifact</h2>
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <svg
                    className="w-5 h-5 text-zinc-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                  <span className="font-medium text-zinc-200 truncate">
                    bundle.tar.zst
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {pointer.bundle.sizeBytes > 0 && (
                    <span className="text-sm text-zinc-500">
                      {formatBytes(pointer.bundle.sizeBytes)}
                    </span>
                  )}
                  <a
                    href={pointer.bundle.url}
                    className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3.5 py-1.5 rounded-lg transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download
                  </a>
                </div>
              </div>
              {pointer.bundle.sha256 && (
                <CopyBox label="SHA-256" value={pointer.bundle.sha256} />
              )}
            </div>
            <p className="mt-4 text-xs text-zinc-500">
              Factory-prep burns ship stable only. The admin TUI pulls this
              archive via{" "}
              <code className="text-zinc-400">
                releases.blazeboard.co/api/releases/bundles/current/stable
              </code>
              .
            </p>
          </section>
        )}

        {(pointer.commitSha || pointer.ciPipelineId) && (
          <section className="text-xs text-zinc-600 flex flex-wrap gap-x-4 gap-y-1 font-mono">
            {pointer.commitSha && (
              <span>commit {pointer.commitSha.slice(0, 12)}</span>
            )}
            {pointer.ciPipelineId && (
              <span>ci-pipeline #{pointer.ciPipelineId}</span>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function ComponentChipLink({
  name,
  version,
  channel,
  pointer,
}: {
  name: string;
  version: string;
  channel: ChannelName;
  pointer: ComponentPointer | null;
}) {
  const isCurrent = pointer?.version === version;
  return (
    <Link
      to={`/releases/${channel}/${version}?component=${name}`}
      className="flex items-center justify-between gap-2 bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 rounded-lg px-3 py-2.5 transition-colors group"
    >
      <div className="min-w-0">
        <p className="text-xs text-zinc-500 capitalize">{name}</p>
        <p className="text-sm font-mono text-zinc-200 group-hover:text-zinc-100">
          v{version}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {isCurrent && (
          <span className="text-[10px] uppercase tracking-wider text-green-400">
            tip
          </span>
        )}
        <svg
          className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </Link>
  );
}

/**
 * Component detail view — advanced/technical surface. Reachable from a
 * bundle's "pinned components" section or from a per-component card on
 * the Releases page. Shows the single artifact with a verify-ready sha256.
 */
function ComponentDetailView({ release }: { release: Release }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 animate-fade-in">
      <BackLink />

      <div className="mb-8">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {release.component && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700">
              {release.component}
            </span>
          )}
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              release.channel === "stable"
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
            }`}
          >
            {release.channel}
          </span>
          {release.isLatest && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Latest
            </span>
          )}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-mono mb-2">
          v{release.version}
        </h1>
        <p className="text-zinc-400">{formatDate(release.date)}</p>
        <p className="mt-3 text-xs text-zinc-500 max-w-xl">
          Component-level detail page. Customers normally upgrade via the
          bundle — this view is for operators verifying an individual
          signed artifact.
        </p>
      </div>

      <div className="space-y-8">
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="font-semibold text-zinc-100 mb-4">Release notes</h2>
          <ReleaseNotesBody notes={release.notes} />
        </section>

        {release.artifacts.length > 0 && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-semibold text-zinc-100 mb-4">Artifacts</h2>
            <div className="space-y-3">
              {release.artifacts.map((artifact) => (
                <div
                  key={artifact.name}
                  className="bg-zinc-800/50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg
                        className="w-5 h-5 text-zinc-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                      </svg>
                      <span className="font-medium text-zinc-200 truncate">
                        {artifact.filename || artifact.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {artifact.size > 0 && (
                        <span className="text-sm text-zinc-500">
                          {formatBytes(artifact.size)}
                        </span>
                      )}
                      <a
                        href={artifact.url}
                        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3.5 py-1.5 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download
                      </a>
                    </div>
                  </div>
                  {artifact.sha256 && (
                    <CopyBox label="SHA-256" value={artifact.sha256} />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-6 h-6 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-zinc-100 mb-2">
          Could not load release
        </h2>
        <p className="text-sm text-zinc-400 mb-6">{message}</p>
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function NotFoundState({ version }: { version: string | undefined }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-6 h-6 text-zinc-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-zinc-100 mb-2">
          Release not found
        </h2>
        <p className="text-sm text-zinc-400 mb-6">
          Version {version ? `v${version}` : "unknown"} does not exist or has
          not been published yet.
        </p>
        <Link
          to="/releases"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
        >
          View all releases
        </Link>
      </div>
    </div>
  );
}
